import React, { useState, useRef, useEffect, useContext, useMemo } from 'react';

import {
  Box,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  Flex,
} from '@chakra-ui/react';

import { NewSearchInput } from './NewSearchInput';
import { SearchHistoryList } from './SearchHistoryList';
import { useAuth } from '../../../Auth';
import { Button } from '../../../Button';
import { useIsMobile } from '../../../hooks';
import { MagnifyingGlass } from '../../../Icons/Icons';
import { useModalContext } from '../../../ModalProvider';
import { useSearchQuery } from '../../../SearchBar/hooks';
import { useAddPastSearch } from '../../../SearchBar/PastSearches/api/addPastSearch';
import { useDeletePastSearch } from '../../../SearchBar/PastSearches/api/deletePastSearch';
import { useGetPastSearches } from '../../../SearchBar/PastSearches/api/getPastSearches';
import { EColor } from '../../../Theme/components/colors';
import { maxWidths } from '../../../utilities';
import { tracker } from '../../../utilities/tracker';
import { NavigationContext } from '../../context/NavigationContext';
import { getLink } from '../../utils';

const MAX_SEARCH_HISTORY_ITEMS = 10;

type NavSearchProps = {
  isExpandedFullNav?: boolean;
  allowCollapsed?: boolean;
  triggerButtonOnly?: boolean;
  className?: string;
  top?: number;
  onExpandToggle?: (isExpanded: boolean) => void;
};

function NewNavSearch({
  isExpandedFullNav = false,
  allowCollapsed = true,
  triggerButtonOnly = false,
  top = 0,
  className,
  onExpandToggle,
}: NavSearchProps) {
  const auth = useAuth();
  const isLoggedIn = !!auth?.profile;
  const { fanaticsCollectBaseUrl } = useModalContext();
  const { pastSearchBaseUrl = '' } = useContext(NavigationContext);
  const { data: pastSearches } = useGetPastSearches(
    pastSearchBaseUrl,
    isLoggedIn
  );
  const { mutateAsync: deleteSearch } = useDeletePastSearch(pastSearchBaseUrl);
  const { mutateAsync: addPastSearch } = useAddPastSearch(pastSearchBaseUrl);

  const savedSearches = useMemo(
    () => pastSearches?.savedSearchList?.map((item) => item.term) ?? [],
    [pastSearches?.savedSearchList]
  );

  const lastThreeList = useMemo(
    () => pastSearches?.lastThreeList?.map((item) => item.term) ?? [],
    [pastSearches?.lastThreeList]
  );

  const initialSearchHistory = [...lastThreeList, ...savedSearches].slice(
    0,
    MAX_SEARCH_HISTORY_ITEMS
  );
  const [searchHistory, setSearchHistory] = useState(initialSearchHistory);

  const isMobile = useIsMobile();
  const pathname =
    typeof window !== 'undefined' ? window.location.pathname : null;
  const [searchText, setSearchText] = useSearchQuery();
  const inputRef = useRef<HTMLInputElement>(null);
  const mobileInputRef = useRef<HTMLInputElement>(null);
  const [openMobileSearch, setOpenMobileSearch] = useState<boolean>(false);

  const searchHistoryDropdownRef = useRef<HTMLDivElement>(null);
  const [openSearchHistory, setOpenSearchHistory] = useState<boolean>(false);
  const [collapsed, setCollapsed] = useState(true);
  const isCollapsed = allowCollapsed && collapsed;

  const [value, setValue] = useState<string | undefined>(searchText ?? '');

  useEffect(() => {
    if (!isExpandedFullNav) {
      setCollapsed(true);
      setOpenSearchHistory(false);
      setOpenMobileSearch(false);
    }
  }, [isExpandedFullNav]);

  useEffect(() => {
    setValue(searchText);
  }, [pathname, searchText]);

  useEffect(() => {
    const newSearchHistory = [...lastThreeList, ...savedSearches].slice(
      0,
      MAX_SEARCH_HISTORY_ITEMS
    );
    setSearchHistory(newSearchHistory);
  }, [lastThreeList, savedSearches]);

  const track = (
    sectionClicked: string,
    meta: { [key: string]: string | undefined | number | null } = {}
  ) => {
    tracker.track('Navbar Search Interaction', {
      sectionClicked,
      ...meta,
    });
  };

  const onInputChange = (newValue?: string) => {
    if (newValue) {
      if (isMobile && !openMobileSearch) {
        setOpenMobileSearch(true);
      } else if (!isMobile && !openSearchHistory) {
        setOpenSearchHistory(true);
      }
    }
    setValue(newValue);
  };

  const handleSearch = (data: string) => {
    addPastSearch(data);
    setOpenSearchHistory(false);
    setOpenMobileSearch(false);
    setCollapsed(true);
    onExpandToggle?.(false);

    track('Search History Selection', {
      value: data,
    });

    window.location.href = getLink(fanaticsCollectBaseUrl, 'weeklySRP', {
      q: data,
    });
  };

  const handleClear = () => {
    setSearchText('');
    setValue('');
    track('Search Clear');
  };

  const onEnter = (data: string | undefined) => {
    const trimmedData = data?.trim();

    track('Search', {
      value: trimmedData,
    });

    setOpenMobileSearch(false);
    setOpenSearchHistory(false);

    if (trimmedData) {
      handleSearch(trimmedData);
    } else {
      handleClear();
    }

    inputRef?.current?.blur();
  };

  const onInputFocus = () => {
    if (!isMobile) {
      setOpenSearchHistory(true);
      onExpandToggle?.(true);
      if (allowCollapsed) {
        setCollapsed(false);
      }
    }
  };

  const onEscape = () => {
    setOpenSearchHistory(false);
    onExpandToggle?.(false);
    inputRef.current?.blur();
  };

  const onSelectFromSearchHistory = (data?: string) => {
    setValue(data);
    setOpenSearchHistory(false);
    setOpenMobileSearch(false);

    if (data) {
      handleSearch(data);
    }
  };

  const onRemoveItemFromHistory = (data?: string) => {
    if (data) {
      deleteSearch(data);
      // update the search history locally
      const updatedLastThree = lastThreeList.filter((term) => term !== data);
      const updatedSaved = savedSearches.filter((term) => term !== data);
      const updatedHistory = [...updatedLastThree, ...updatedSaved].slice(
        0,
        MAX_SEARCH_HISTORY_ITEMS
      );
      setSearchHistory(updatedHistory);
    }
  };

  const onInputClick = () => {
    if (isMobile) {
      setOpenMobileSearch(true);
      mobileInputRef.current?.focus();
    }
  };

  const onCloseMobileSearch = () => {
    setOpenMobileSearch(false);
    onEscape();
  };

  useEffect(() => {
    let frame: number | undefined;
    if (openMobileSearch) {
      frame = requestAnimationFrame(() => {
        mobileInputRef.current?.focus();
      });
    }
    return () => {
      if (frame !== undefined) {
        cancelAnimationFrame(frame);
      }
    };
  }, [openMobileSearch]);

  useEffect(() => {
    const checkIfClickIsInSearchElements = (event: MouseEvent) => {
      const { target } = event;
      const targetElement = target as Node;
      const isInInput = inputRef.current?.contains(targetElement);
      const isInDropdown =
        searchHistoryDropdownRef.current?.contains(targetElement);

      const targetIsSearchRelated =
        targetElement && (isInInput || isInDropdown);

      if (!targetIsSearchRelated) {
        setOpenSearchHistory(false);
        onExpandToggle?.(false);
      }
    };

    document.addEventListener('mousedown', checkIfClickIsInSearchElements);

    return () => {
      document.removeEventListener('mousedown', checkIfClickIsInSearchElements);
    };
  }, [onExpandToggle]);

  return (
    <Box
      alignItems="center"
      className={className}
      display="flex"
      flexGrow={isCollapsed ? 0 : 1}
      maxW={
        isCollapsed && !openMobileSearch
          ? isMobile
            ? undefined
            : '240px'
          : undefined
      }
      position="relative"
      w={triggerButtonOnly ? 'fit-content' : '100%'}
    >
      {triggerButtonOnly ? (
        <Button
          border="none"
          borderWidth={0}
          padding={0}
          onClick={() => setOpenMobileSearch(true)}
        >
          <MagnifyingGlass color={EColor.Black} height="20px" width="20px" />
        </Button>
      ) : (
        <Flex
          maxW={isCollapsed ? (isMobile ? undefined : '240px') : undefined}
          w="100%"
        >
          <NewSearchInput
            isCollapsed={isCollapsed}
            placeholder="Search"
            ref={inputRef}
            showClearInputButton={!!value && !isCollapsed}
            value={isExpandedFullNav ? value : ''}
            onChange={onInputChange}
            onClear={handleClear}
            onClick={onInputClick}
            onEnter={onEnter}
            onEscape={onEscape}
            onFocus={onInputFocus}
          />
        </Flex>
      )}

      {openSearchHistory && searchHistory.length > 0 && (
        <Box
          bg="white"
          boxShadow="0px 4px 16px 0px rgba(0, 0, 0, 0.12)"
          left="0"
          maxH={`min(320px, calc(100vh - ${top}px))`}
          overflowY="auto"
          position="fixed"
          ref={searchHistoryDropdownRef}
          right="0"
          top={top}
          w="100%"
          zIndex={1000}
        >
          <Flex
            alignItems="center"
            margin="0 auto"
            maxW={maxWidths}
            px="16px"
            py="24px"
            w="100%"
          >
            <SearchHistoryList
              handleRemove={onRemoveItemFromHistory}
              handleSearch={onSelectFromSearchHistory}
              history={searchHistory}
            />
          </Flex>
        </Box>
      )}

      <Modal
        isOpen={openMobileSearch}
        size="full"
        onClose={onCloseMobileSearch}
      >
        <ModalOverlay />
        <ModalContent
          alignItems="center"
          bg="transparent"
          h="100%"
          rounded="none"
        >
          <Flex
            bg="white"
            flexDir="column"
            h="100%"
            justifyContent="center"
            p="8px"
            w="100%"
          >
            <ModalHeader padding="8px">
              <Flex alignItems="center" gap={2} w="full">
                <NewSearchInput
                  autoFocus
                  placeholder="Search"
                  ref={mobileInputRef}
                  showClearInputButton={!!value}
                  value={value}
                  onChange={onInputChange}
                  onClear={handleClear}
                  onEnter={onEnter}
                />
                <Button
                  border="none"
                  color={EColor.Black}
                  fontWeight="normal"
                  h="40px"
                  paddingX="0"
                  textTransform="none"
                  onClick={onCloseMobileSearch}
                >
                  Cancel
                </Button>
              </Flex>
            </ModalHeader>
            <ModalBody
              css={{ scrollbarWidth: 'none' }}
              h="full"
              overflowY="auto"
            >
              <SearchHistoryList
                handleRemove={onRemoveItemFromHistory}
                handleSearch={onSelectFromSearchHistory}
                history={searchHistory}
              />
            </ModalBody>
          </Flex>
        </ModalContent>
      </Modal>
    </Box>
  );
}

export { NewNavSearch };
